/** Formulário de troca de senha: obrigatório no primeiro acesso e no menu do usuário. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Rotulo } from "@/components/ui/seletor";
import { api, ErroApi } from "@/lib/api";
import { chaveSessao } from "@/lib/sessao";

export function FormTrocarSenha({
  onConcluido,
  provisoria = false,
}: {
  onConcluido?: () => void;
  provisoria?: boolean;
}) {
  const qc = useQueryClient();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const salvar = useMutation({
    mutationFn: () => api.post("/usuarios/eu/senha", { atual, nova }),
    onSuccess: () => {
      toast.success("Senha atualizada!");
      void qc.invalidateQueries({ queryKey: chaveSessao });
      onConcluido?.();
    },
  });
  const diferente = confirma.length > 0 && confirma !== nova;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (nova !== confirma) return;
    salvar.mutate();
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <Rotulo texto={provisoria ? "Senha provisória" : "Senha atual"}>
        <Campo
          type="password"
          autoComplete="current-password"
          required
          value={atual}
          onChange={(e) => setAtual(e.target.value)}
          autoFocus
        />
      </Rotulo>
      <Rotulo texto="Nova senha" ajuda="Pelo menos 10 caracteres.">
        <Campo
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={nova}
          onChange={(e) => setNova(e.target.value)}
        />
      </Rotulo>
      <Rotulo texto="Repita a nova senha">
        <Campo
          type="password"
          autoComplete="new-password"
          required
          value={confirma}
          onChange={(e) => setConfirma(e.target.value)}
          aria-invalid={diferente}
        />
      </Rotulo>
      {diferente && <p className="text-sm text-danger-500">As senhas não conferem.</p>}
      {salvar.error && (
        <p role="alert" className="rounded-md bg-danger-100 px-3 py-2 text-sm text-danger-500">
          {salvar.error instanceof ErroApi
            ? salvar.error.message
            : "Não foi possível trocar a senha."}
        </p>
      )}
      <Botao
        type="submit"
        className="w-full justify-center"
        disabled={salvar.isPending || diferente}
      >
        Salvar nova senha
      </Botao>
    </form>
  );
}

/** Tela cheia do primeiro acesso (senha provisória). */
export function TelaTrocarSenhaObrigatoria({ nome }: { nome: string }) {
  const qc = useQueryClient();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-gradient-to-b from-gold-200 to-white px-4">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-neutral-200 bg-white p-6 shadow-md sm:p-8">
        <div className="space-y-1 text-center">
          <img
            src="/logo-yourdress-wordmark.png"
            alt="YourDress"
            className="mx-auto mb-3 h-9 object-contain"
          />
          <h1 className="text-xl">Olá, {nome.split(" ")[0]}!</h1>
          <p className="text-sm text-neutral-500">Para começar, defina a sua senha.</p>
        </div>
        <FormTrocarSenha provisoria />
        <button
          onClick={() =>
            void api.post("/auth/logout").finally(() => qc.setQueryData(chaveSessao, null))
          }
          className="w-full text-center text-sm text-neutral-500 hover:text-neutral-800"
        >
          Sair
        </button>
      </div>
    </div>
  );
}
